import {
  ReplaceWhitelistBody,
  ReplaceWhitelistBodyKey,
  WhitelistItem,
} from 'multirpc-sdk';
import { useCallback, useMemo } from 'react';

import { getUniqueArray } from 'modules/common/utils/getUniqueArray';
import { useAccount } from 'modules/account/hooks/useAccount';

import { useReplaceWhitelistMutation } from '../actions/replaceWhitelist';
import { useSelectedProject } from './useSelectedProject';
import { mapMultichainToAapiV2 } from '../utils/mapMultichainToAapiV2';

export interface UseReplaceWhitelistParams {
  onSuccess?: () => void | Promise<void>;
  whitelist: WhitelistItem[];
  nestedUserEndpointToken?: string;
}

const getReplaceWhitelistBody = (whitelist: WhitelistItem[]) =>
  whitelist.reduce<ReplaceWhitelistBody>(
    (result, item) => {
      const type = item.type as ReplaceWhitelistBodyKey;

      const preapredBlockchain = mapMultichainToAapiV2(item.blockchain);

      result[type] = {
        ...result[type],
        [preapredBlockchain]: getUniqueArray([
          ...(result[type][preapredBlockchain] || []),
          ...item.list,
        ]),
      };

      return result;
    },
    { address: {}, ip: {}, referer: {} },
  );

export const useReplaceWhitelistHandler = ({
  nestedUserEndpointToken,
  onSuccess,
  whitelist: whitelistItems,
}: UseReplaceWhitelistParams) => {
  const { group } = useAccount();
  const { project } = useSelectedProject({
    nestedEndpointToken: nestedUserEndpointToken,
  });

  const [replace, { isLoading: isReplacing }] = useReplaceWhitelistMutation();

  const userEndpointToken = useMemo(
    () => project?.userEndpointToken,
    [project?.userEndpointToken],
  );

  const whitelist = useMemo(
    () => getReplaceWhitelistBody(whitelistItems),
    [whitelistItems],
  );

  const handleReplaceWhitelist = useCallback(async () => {
    if (userEndpointToken) {
      const result = await replace({
        params: { group, token: userEndpointToken, whitelist },
      });

      if (!('error' in result)) {
        onSuccess?.();
      }
    }
  }, [group, onSuccess, replace, userEndpointToken, whitelist]);

  return { handleReplaceWhitelist, isReplacing };
};
